import { Building2, Plus, ShieldCheck, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/Button';
import { formatNumber } from '@/lib/format';
import type { PlatformStats } from '@/types/institute';
import { useIsSuperAdmin } from '../hooks/useIsSuperAdmin';
import { DeckSurface } from './DeckSurface';

interface DeckHeroProps {
  operatorName: string;
  operatorEmail: string;
  stats: PlatformStats | null;
  isLoading: boolean;
}

export function DeckHero({ operatorName, operatorEmail, stats, isLoading }: DeckHeroProps) {
  const navigate = useNavigate();
  const isSuperAdmin = useIsSuperAdmin();
  const firstName = operatorName.trim().split(' ')[0] || 'there';

  return (
    <DeckSurface className="rounded-2xl border border-accent-line p-4 sm:p-5 shadow-card">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        {/* Left Side: Headline & Quick Actions */}
        <div className="flex min-w-0 max-w-2xl flex-col gap-2">
          <div className="flex items-center gap-2">
            <span className="flex w-fit items-center gap-2 rounded-pill border border-accent-line bg-surface px-3 py-1 text-2xs font-bold uppercase tracking-label text-accent-2">
              <span className="relative grid h-2 w-2 place-items-center" aria-hidden>
                <span className="absolute inset-0 animate-glow rounded-full bg-accent" />
                <span className="h-1 w-1 rounded-full bg-accent" />
              </span>
              PLATFORM CONTROL CENTRE
            </span>
          </div>

          <h1 className="text-xl sm:text-2xl font-black tracking-tight text-ink">
            Good to see you, <span className="text-accent">{firstName}.</span>
          </h1>

          <p className="max-w-xl text-xs sm:text-[13px] text-ink-2 leading-normal">
            {isLoading || !stats ? (
              'Counting every institute, every child and every commitment on the platform…'
            ) : (
              <>
                <strong className="font-bold text-ink">{formatNumber(stats.activeInstitutes)}</strong>{' '}
                {stats.activeInstitutes === 1 ? 'institute is' : 'institutes are'} live,{' '}
                <strong className="font-bold text-warn">{formatNumber(stats.pendingSetup)}</strong>{' '}
                still being set up, and{' '}
                {/* Children at the live institutes only, so the two figures in the
                    sentence describe the same schools. */}
                <strong className="font-bold text-ink">{formatNumber(stats.studentsAtLiveInstitutes)}</strong>{' '}
                {stats.studentsAtLiveInstitutes === 1 ? 'child is' : 'children are'} enrolled at the
                live ones.
              </>
            )}
          </p>

          <div className="mt-1 flex flex-wrap items-center gap-2">
            {/* Creating an institute is super-admin work; support never sees the button. */}
            {isSuperAdmin && (
              <Button
                leftIcon={<Plus className="h-3.5 w-3.5" />}
                onClick={() => navigate('/platform/institutes/new')}
                className="h-8 rounded-lg bg-accent hover:bg-accent/90 text-white px-3 text-sm font-bold shadow-xs"
              >
                Add institute
              </Button>
            )}
            <button
              type="button"
              onClick={() => navigate('/platform/institutes')}
              className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-line bg-surface px-3 text-sm font-bold text-ink-2 shadow-xs transition-all hover:border-accent hover:text-accent"
            >
              <Building2 className="h-3.5 w-3.5" />
              Browse customers
            </button>
          </div>
        </div>

        {/* Right Side: Floating Signed in As Card */}
        <div className="w-full shrink-0 rounded-xl border border-line bg-surface p-3 sm:p-3.5 shadow-xs lg:w-[240px]">
          <div className="flex items-center gap-1.5 text-3xs font-bold uppercase tracking-wider text-ink-3">
            <ShieldCheck className="h-3.5 w-3.5 text-accent" />
            SIGNED IN AS
          </div>

          {operatorName && (
            <p className="mt-0.5 truncate text-[13px] font-bold text-ink">{operatorName}</p>
          )}
          {operatorEmail && (
            <p className="truncate text-[11px] text-ink-3 font-medium">{operatorEmail}</p>
          )}

          <div className="mt-2 flex items-start gap-1.5 border-t border-line pt-2 text-[11px] leading-snug text-ink-3">
            <Sparkles className="mt-0.5 h-3 w-3 shrink-0 text-accent" />
            Institute data is only ever reached through an action we record against your name.
          </div>
        </div>
      </div>
    </DeckSurface>
  );
}
